// `TypstLayout` and `PrintLayout` (SuiteCompile/PrintLayout.swift): a print compile's pages, with each line traced to
// the paragraph, heading or scene break it came from, and the measurements the copyfit loop and preflight use.
// Line offsets inside a paragraph are inexact while the no-hyphenation show rule splits text (web plan §6.1), so
// lines map to regions only, never to exact positions.
import { blockKey, type BlockKey, type Region, type TypstDocument } from './writer.ts';

/** TypstKit's layout JSON (`@writing-suite/typst`'s `TypstLayout`, restated so this package needn't depend on it). */
/** One run of a line (with TypstKit's flag 2): where it sits, its face and style, its word spaces as [x, width]. */
export interface LayoutRun { x: number; width: number; size: number; text: string; gaps: number[][]; font: string; italic: boolean; weight: number }

export interface TypstLayout {
  pages: {
    width: number;
    height: number;
    lines: { x: number; y: number; width: number; size: number; text: string; start: number | null; end: number | null; gaps: number[][]; font: string; runs?: LayoutRun[] }[];
  }[];
}

/** One laid-out line. */
export interface PrintLine {
  /** 0-based physical page. */
  page: number;
  x: number;
  y: number;
  width: number;
  size: number;
  text: string;
  /** Index into `regions`; null for running heads, folios and the title page. */
  region: number | null;
  gaps: { x: number; width: number }[];
  font: string;
}

const WHITE = /\p{White_Space}+/u;

/** `Line.words`. */
export const lineWords = (l: PrintLine) => l.text.split(WHITE).filter((w) => w !== '').length;
const trimSpaces = (s: string) => s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
/** `Line.hyphenated`: ends in a hyphen Typst added (a soft hyphen in its text). */
export const hyphenated = (l: PrintLine) => trimSpaces(l.text).endsWith('\u00AD');
/** `Line.endsWithHyphen`: any hyphen, added or written. */
export const endsWithHyphen = (l: PrintLine) => {
  const t = trimSpaces(l.text);
  return t.endsWith('\u00AD') || t.endsWith('-') || t.endsWith('‐');
};
/** `Line.readable`: the text as a reader sees it, for quoting. */
export const readable = (l: PrintLine) => trimSpaces(l.text.replaceAll('\u00AD', ''));

export interface Metrics {
  /** The text block's width, a paragraph line's height and a typical word's width, in points. */
  measure: number;
  lineHeight: number;
  wordWidth: number;
}

/** Facing pages whose last lines don't align. */
export interface Spread {
  /** 0-based pages: verso is odd (physical page 2, 4…), recto the page after. */
  verso: number;
  recto: number;
  short: number;
  /** How many lines short. */
  lines: number;
  /** Index into `lines` of the short page's last line. */
  lastLine: number;
}

const segmenter = new Intl.Segmenter();
/** Swift's `String.count`: Characters (grapheme clusters). */
export function characterCount(s: string): number {
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7f]*$/.test(s) && !s.includes('\r\n')) return s.length;
  let n = 0;
  for (const _ of segmenter.segment(s)) n++;
  return n;
}

const median = (v: number[]) => (v.length ? [...v].sort((a, b) => a - b)[Math.floor(v.length / 2)] : null);
/** Swift's `rounded()`: half away from zero. */
const round = (x: number) => (x < 0 ? -Math.round(-x) : Math.round(x));

export class PrintLayout {
  pageSizes: { width: number; height: number }[];
  /** Every line, in reading order. */
  lines: PrintLine[];
  regions: Region[];
  /** Blank scene breaks given an ornament because they fell at a page edge. */
  ornamentedBreaks = 0;
  /** Paragraphs tracked looser to balance a spread. */
  loosenedParagraphs = 0;

  constructor(document: TypstDocument, layout: TypstLayout) {
    this.regions = document.regions;
    this.pageSizes = layout.pages.map((p) => ({ width: p.width, height: p.height }));
    const starts = document.regions.map((r) => r.start);
    const lines: PrintLine[] = [];
    layout.pages.forEach((page, p) => {
      for (const l of page.lines) {
        let region: number | null = null;
        if (l.start !== null && l.start !== undefined) {
          // The last region starting at or before the line's first glyph.
          let lo = 0, hi = starts.length;
          while (lo < hi) { const mid = (lo + hi) >> 1; if (starts[mid] <= l.start) lo = mid + 1; else hi = mid; }
          if (lo > 0 && l.start < document.regions[lo - 1].end) region = lo - 1;
        }
        lines.push({ page: p, x: l.x, y: l.y, width: l.width, size: l.size, text: l.text, region,
          gaps: l.gaps.filter((g) => g.length === 2).map((g) => ({ x: g[0], width: g[1] })), font: l.font });
      }
    });
    this.lines = lines;
  }

  get pageCount(): number {
    return this.pageSizes.length;
  }

  /** Indices into `lines` of each region's lines, in order (regions ascending). */
  linesByRegion(): Map<number, number[]> {
    const result = new Map<number, number[]>();
    this.lines.forEach((line, i) => {
      if (line.region === null) return;
      const list = result.get(line.region);
      if (list) list.push(i); else result.set(line.region, [i]);
    });
    return new Map([...result.entries()].sort((a, b) => a[0] - b[0]));
  }

  /** Blank scene breaks (by break index) whose neighbouring text is on different pages, so the break falls at a page
   *  edge and can't be seen. */
  invisibleBreaks(): number[] {
    const firstPage = new Map<number, number>(), lastPage = new Map<number, number>();
    for (const line of this.lines) {
      if (line.region === null) continue;
      if (!firstPage.has(line.region)) firstPage.set(line.region, line.page);
      lastPage.set(line.region, line.page);
    }
    const placed = [...firstPage.keys()].sort((a, b) => a - b);
    const result: number[] = [];
    this.regions.forEach((region, r) => {
      if (region.kind.type !== 'sceneBreak' || !region.kind.blank) return;
      // The nearest regions with lines on either side (regions and lines are both in reading order).
      let lo = 0, hi = placed.length;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (placed[mid] < r) lo = mid + 1; else hi = mid; }
      let next = lo;
      while (next < placed.length && placed[next] <= r) next += 1;
      if (lo === 0 || next >= placed.length) return;
      const before = lastPage.get(placed[lo - 1]), after = firstPage.get(placed[next]);
      if (before === undefined || after === undefined) return;
      if (before !== after) result.push(region.kind.index);
    });
    return result;
  }

  metrics(): Metrics {
    const full: PrintLine[] = [], steps: number[] = [];
    for (const [r, indices] of this.linesByRegion()) {
      if (this.regions[r].kind.type !== 'paragraph') continue;
      indices.forEach((i, n) => {
        if (n < indices.length - 1) full.push(this.lines[i]);
        if (n > 0 && this.lines[indices[n - 1]].page === this.lines[i].page) steps.push(this.lines[i].y - this.lines[indices[n - 1]].y);
      });
    }
    const words = full.reduce((s, l) => s + lineWords(l), 0);
    const widest = this.lines.length ? Math.max(...this.lines.map((l) => l.width)) : null;
    return {
      measure: median(full.map((l) => l.width)) ?? widest ?? 300,
      lineHeight: median(steps) ?? 14,
      wordWidth: words > 0 ? full.reduce((s, l) => s + l.width, 0) / words : 30,
    };
  }

  /** Facing pages that should align but don't. The foot of a chapter, or a page before a blank one, may run short. */
  imbalancedSpreads(metrics: Metrics = this.metrics()): Spread[] {
    const body = this.lines.map((_, i) => i).filter((i) => this.lines[i].region !== null);
    const lastOnPage = new Map<number, number>(), position = new Map<number, number>();
    body.forEach((i, n) => { lastOnPage.set(this.lines[i].page, i); position.set(i, n); });
    const endsChapter = (page: number) => {
      const last = lastOnPage.get(page);
      const n = last === undefined ? undefined : position.get(last);
      if (n === undefined || n + 1 >= body.length) return true;
      const next = this.lines[body[n + 1]];
      return (next.region !== null ? this.regions[next.region].kind.type === 'heading' : true) || next.page > page + 1;
    };
    const result: Spread[] = [];
    for (let verso = 1; verso < this.pageCount - 1; verso += 2) {
      const recto = verso + 1;
      const v = lastOnPage.get(verso), r = lastOnPage.get(recto);
      if (v === undefined || r === undefined || endsChapter(verso) || endsChapter(recto)) continue;
      const difference = this.lines[v].y - this.lines[r].y;
      if (!(Math.abs(difference) > metrics.lineHeight / 2)) continue;
      result.push({ verso, recto, short: difference < 0 ? verso : recto,
        lines: Math.max(1, Math.trunc(round(Math.abs(difference) / metrics.lineHeight))), lastLine: difference < 0 ? v : r });
    }
    return result;
  }

  /** Paragraphs that a little extra tracking would push onto one more line, so `page` gains a line at its foot, with
   *  the tracking (em) each needs, best first. One ending on the page gains a line and nothing after it moves; one
   *  ending up to two pages earlier (same chapter) pushes a line across each page break between, so ranks lower. */
  loosenCandidates(page: number, metrics: Metrics, maximum = 0.03): { key: BlockKey; tracking: number }[] {
    const byRegion = this.linesByRegion();
    // Only paragraphs after the heading that opens the page's chapter.
    let opener = -1;
    for (let r = this.regions.length - 1; r >= 0; r--) {
      const first = byRegion.get(r)?.[0];
      if (this.regions[r].kind.type === 'heading' && first !== undefined && this.lines[first].page <= page) { opener = r; break; }
    }
    const result: { key: BlockKey; tracking: number; score: number }[] = [];
    for (const [r, indices] of byRegion) {
      if (!(r > opener && this.regions[r].kind.type === 'paragraph' && indices.length >= 2)) continue;
      const ls = indices.map((i) => this.lines[i]);
      const last = ls[ls.length - 1];
      const block = this.regions[r].block;
      if (!(last.page <= page && last.page >= page - 2) || block === null) continue;
      const characters = ls.reduce((s, l) => s + characterCount(l.text), 0);
      // Enough to overflow the last line by a word, with room for the line breaker's give.
      const extra = (metrics.measure - last.width + metrics.wordWidth) * 1.3;
      const tracking = extra / Math.max(1, characters) / Math.max(1, last.size);
      if (tracking <= maximum) result.push({ key: blockKey(this.regions[r].section, block), tracking, score: tracking + 0.008 * (page - last.page) });
    }
    return result.sort((a, b) => a.score - b.score).map(({ key, tracking }) => ({ key, tracking }));
  }
}
